
import { defineComponent, reactive, ref } from "vue";
import { RouterLink } from "vue-router";

import { Icon } from "../components/icons";
import { categories } from "../data/categories";
import common from "../styles/common.module.less";
import style from "./style.module.less";
import {
  DESC_MAX,
  emptyPublishForm,
  PublishErrors,
  PublishForm,
  TITLE_MAX,
  TITLE_MIN,
  hasErrors,
  validatePublishForm,
} from "./validate";

export default defineComponent({
  name: "XiyouPublish",
  setup() {
    const form = reactive<PublishForm>(emptyPublishForm());
    const errors = ref<PublishErrors>({});
    const submitted = ref(false);

    const subCategories = () => {
      const category = categories.find((item) => item.slug === form.category);
      return category ? category.subCategories : [];
    };

    const selectCategory = (slug: string) => {
      form.category = slug;
      form.subCategory = "";
      errors.value = { ...errors.value, category: undefined };
    };

    const selectSubCategory = (slug: string) => {
      form.subCategory = slug;
      errors.value = { ...errors.value, subCategory: undefined };
    };

    // 仅做前端校验与页内提示：不写入任何存储、不发起任何请求
    const submit = () => {
      const result = validatePublishForm(form);
      errors.value = result;
      if (hasErrors(result)) return;
      submitted.value = true;
    };

    const again = () => {
      Object.assign(form, emptyPublishForm());
      errors.value = {};
      submitted.value = false;
    };

    const renderField = (
      key: keyof PublishForm,
      label: string,
      required: boolean,
      input: () => unknown
    ) => (
      <div class={style.row} key={key}>
        <label class={style.label}>
          {label}
          {required ? <em>*</em> : null}
        </label>
        <div class={style.field}>
          {input()}
          {errors.value[key] ? (
            <div class={style.error}>{errors.value[key]}</div>
          ) : null}
        </div>
      </div>
    );

    return () => (
      <div class={common.shell}>
        {submitted.value ? (
          <div class={[common.card, style.success]}>
            <span class={style.successIcon}>
              <Icon name="check" />
            </span>
            <div class={style.successTitle}>信息已提交，审核通过后展示</div>
            <div class={style.successDesc}>
              平台将在 1
              个工作日内完成审核；审核通过后，信息会在信息门户与对应分类中展示。若审核未通过，请按提示修改后重新提交。
            </div>
            <div class={style.successActions}>
              <button class={common.btn} type="button" onClick={again}>
                再发一条
              </button>
              <RouterLink
                class={[common.btn, common.btnPrimary]}
                to={{ name: "XiyouPortal" }}
              >
                去信息门户
              </RouterLink>
            </div>
          </div>
        ) : (
          <div class={style.page}>
            <h1 class={common.pageTitle}>信息发布</h1>
            <p class={common.pageSubtitle}>
              信息提交后需通过审核才会展示，发布前建议先阅读
              <RouterLink
                class={style.link}
                to={{ name: "XiyouReviewMechanism" }}
              >
                《信息审核机制》
              </RouterLink>
              与
              <RouterLink class={style.link} to={{ name: "XiyouFees" }}>
                《收费规则》
              </RouterLink>
              。
            </p>

            <div class={[common.card, style.form]}>
              {renderField("category", "信息分类", true, () => (
                <div class={common.tabs}>
                  {categories.map((category) => (
                    <button
                      key={category.slug}
                      type="button"
                      class={[
                        common.tab,
                        category.slug === form.category
                          ? common.tabActive
                          : null,
                      ]}
                      onClick={() => selectCategory(category.slug)}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              ))}

              {renderField("subCategory", "子分类", false, () => (
                <div class={common.tabs}>
                  <button
                    type="button"
                    class={[
                      common.tab,
                      form.subCategory === "" ? common.tabActive : null,
                    ]}
                    onClick={() => selectSubCategory("")}
                  >
                    不限
                  </button>
                  {subCategories().map((sub) => (
                    <button
                      key={sub.slug}
                      type="button"
                      class={[
                        common.tab,
                        sub.slug === form.subCategory ? common.tabActive : null,
                      ]}
                      onClick={() => selectSubCategory(sub.slug)}
                    >
                      {sub.name}
                    </button>
                  ))}
                </div>
              ))}

              {renderField("title", "信息标题", true, () => (
                <input
                  class={common.input}
                  maxlength={TITLE_MAX}
                  value={form.title}
                  placeholder={`${TITLE_MIN}-${TITLE_MAX} 个字，品类 + 关键卖点`}
                  onInput={(event: Event) => {
                    form.title = (event.target as HTMLInputElement).value;
                  }}
                />
              ))}

              {renderField("description", "详细描述", true, () => (
                <textarea
                  class={common.textarea}
                  rows={5}
                  maxlength={DESC_MAX}
                  value={form.description}
                  placeholder="成色、配置、瑕疵、交易方式等，越具体越容易通过审核"
                  onInput={(event: Event) => {
                    form.description = (
                      event.target as HTMLTextAreaElement
                    ).value;
                  }}
                />
              ))}

              {renderField("price", "价格", true, () => (
                <input
                  class={common.input}
                  value={form.price}
                  placeholder="如 1500元、3200元/月、面议"
                  onInput={(event: Event) => {
                    form.price = (event.target as HTMLInputElement).value;
                  }}
                />
              ))}

              {renderField("contactName", "联系人", true, () => (
                <input
                  class={common.input}
                  value={form.contactName}
                  placeholder="请填写联系人称呼"
                  onInput={(event: Event) => {
                    form.contactName = (event.target as HTMLInputElement).value;
                  }}
                />
              ))}

              {renderField("contactPhone", "联系电话", true, () => (
                <input
                  class={common.input}
                  value={form.contactPhone}
                  placeholder="手机号或座机号（展示时会做脱敏处理）"
                  onInput={(event: Event) => {
                    form.contactPhone = (
                      event.target as HTMLInputElement
                    ).value;
                  }}
                />
              ))}

              <div class={style.actions}>
                <button
                  class={[common.btn, common.btnPrimary, style.submit]}
                  type="button"
                  onClick={submit}
                >
                  提交审核
                </button>
                <span class={style.tip}>
                  平台将在 1 个工作日内完成审核，审核通过后展示
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
});
